/**
 * LocalStorage utility for persisting data across browser sessions
 */

interface StorageData<T = any> {
  data: T
  timestamp: number
}

/**
 * Save data to localStorage with automatic JSON serialization
 */
export function saveToLocalStorage<T>(key: string, data: T): boolean {
  try {
    const storageData: StorageData<T> = {
      data,
      timestamp: Date.now()
    }
    localStorage.setItem(key, JSON.stringify(storageData))
    return true
  } catch (error) {
    console.error('Failed to save to localStorage:', error)
    return false
  }
}

/**
 * Load data from localStorage with automatic JSON parsing
 * Returns null if key doesn't exist or data is expired
 */
export function loadFromLocalStorage<T>(
  key: string,
  maxAgeMs: number = 24 * 60 * 60 * 1000 * 3 // 3 days max age
): T | null {
  try {
    const item = localStorage.getItem(key)
    if (!item) return null

    const storageData: StorageData<T> = JSON.parse(item)

    if (Date.now() - storageData.timestamp > maxAgeMs) {
      localStorage.removeItem(key)
      return null
    }

    return storageData.data
  } catch (error) {
    console.error('Failed to load from localStorage:', error)
    return null
  }
}

/**
 * Remove data from localStorage
 */
export function removeFromLocalStorage(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error('Failed to remove from localStorage:', error)
  }
}
